import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AOS from 'aos';
import { FaArrowRight, FaUsers, FaBriefcase, FaLaptopCode, FaGlobe, FaClock } from 'react-icons/fa';

const STAFFING_SERVICES = [
  { icon: <FaBriefcase />, title: 'Permanent Staffing', desc: 'Find the right permanent tech talent for your organization with our deep candidate network.', color: '#1e40af' },
  { icon: <FaClock />, title: 'Contract Staffing', desc: 'Flexible contract engineers and developers for project-based or time-bound engagements.', color: '#0891b2' },
  { icon: <FaLaptopCode />, title: 'IT Staffing', desc: 'Specialized IT professionals for roles in development, QA, DevOps, data science, and more.', color: '#7c3aed' },
  { icon: <FaGlobe />, title: 'Global Talent Network', desc: 'Access to a global pool of tech professionals for remote, hybrid, and on-site requirements.', color: '#059669' },
];

const TalentForce = () => {
  useEffect(() => { AOS.init({ duration: 900, once: true, offset: 80 }); }, []);

  return (
    <div style={{ background: 'var(--color-bg)' }}>
      <section className="page-hero" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #064e3b 50%, #0e7490 100%)' }}>
        <div className="page-hero-overlay" />
        <Container>
          <div className="page-hero-content" data-aos="fade-up">
            <div className="breadcrumb-vk"><Link to="/">Home</Link> / <span>TalentForce</span></div>
            <h1 className="page-hero-title">TalentForce — <span>IT Staffing & Workforce</span></h1>
            <p className="page-hero-desc">
              Connecting world-class tech talent with forward-thinking enterprises globally. From permanent hiring to contract staffing — we fill your talent gaps fast.
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.75rem', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary-vk">Hire Talent <FaArrowRight /></Link>
              <Link to="/contact" className="btn-outline-vk">Submit Resume <FaUsers /></Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Stats */}
      <section style={{ padding: '2.5rem 0', background: 'var(--color-bg)', borderBottom: '1px solid var(--color-border)' }}>
        <Container>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem', textAlign: 'center' }}>
            {[
              ['33+', 'Placed Professionals', '#059669'], ['50+', 'Hiring Partners', '#1e40af'],
              ['7 Days', 'Avg. Time to Fill', '#0891b2'], ['98%', 'Retention Rate', '#7c3aed'],
            ].map(([val, label, color], i) => (
              <div key={i} data-aos="fade-up" data-aos-delay={i * 80} style={{ padding: '1.5rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: 900, background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{val}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', fontWeight: 500, marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Services */}
      <section className="section-py" style={{ background: 'var(--color-bg-alt)' }}>
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">Staffing Services</span>
            <h2 className="section-title">End-to-End <span>Workforce Solutions</span></h2>
            <p className="section-subtitle">We help you build, scale, and manage a world-class technology team.</p>
          </div>
          <Row className="g-4">
            {STAFFING_SERVICES.map((s, i) => (
              <Col md={6} key={i} data-aos="fade-up" data-aos-delay={i * 100}>
                <div className="vk-card" style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', height: '100%' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 13, background: `${s.color}12`, color: s.color, display: 'grid', placeItems: 'center', fontSize: '1.375rem', flexShrink: 0 }}>{s.icon}</div>
                  <div>
                    <h4 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--color-text)', marginBottom: '0.5rem' }}>{s.title}</h4>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How it works */}
      <section className="section-py" style={{ background: 'var(--color-bg)' }}>
        <Container>
          <div className="section-header text-center" data-aos="fade-up">
            <span className="section-badge">How It Works</span>
            <h2 className="section-title">Hire in <span>3 Simple Steps</span></h2>
          </div>
          <Row className="g-4 justify-content-center">
            {[
              { step: '01', title: 'Share Requirements', desc: 'Tell us about the role, skills needed, timeline, and budget.', color: '#1e40af' },
              { step: '02', title: 'Shortlisted Candidates', desc: 'We present pre-vetted, interview-ready candidates within 3-5 days.', color: '#0891b2' },
              { step: '03', title: 'Hire & Onboard', desc: 'Select your candidates and we handle the rest — paperwork to onboarding.', color: '#059669' },
            ].map((s, i) => (
              <Col md={4} key={i} data-aos="fade-up" data-aos-delay={i * 100}>
                <div className="vk-card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', fontWeight: 900, background: 'var(--gradient-primary)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: '0.75rem' }}>{s.step}</div>
                  <h4 style={{ fontWeight: 700, color: 'var(--color-text)', marginBottom: '0.5rem' }}>{s.title}</h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>{s.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="cta-section" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #064e3b 50%, #0e7490 100%)' }}>
        <Container className="text-center" data-aos="zoom-in">
          <h2 className="cta-title">Find Your <span>Next Tech Hire</span></h2>
          <p className="cta-desc">Let TalentForce connect you with the talent you need to build great products.</p>
          <div className="cta-actions">
            <Link to="/contact" className="btn-primary-vk">Hire Talent <FaArrowRight /></Link>
            <Link to="/contact" className="btn-outline-vk">Submit Resume</Link>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default TalentForce;
